import { create } from 'zustand';

import { DEFAULT_INPUT_STATUS } from '~/constants';
import { OPTION_NONE } from '~/constants/product';

import type {
  TypeProductModalErrorFieldRefs,
  TypeProductModalInput,
  TypeProductModalRequiredInputState,
} from '~/types/product.types';

export const PRODUCT_MODAL_INITIAL_VALUE: TypeProductModalInput = {
  supplierCodes: null,
  skuCode: '',
  skuName: '',
  combinedPackingAvailability: 'N',
  useMethodCode: 'Y',
  shelfLifeType: OPTION_NONE,
  availableDays: null,
  sellAvailableDays: null,
  sellDeadlineDays: null,
  temperatureType: null,
  mainCategoryCode: null,
  middleCategoryCode: null,
  thirdCategoryCode: null,
  fourthCategoryCode: null,
  isOneDayProduct: false,
  sellingType: 'STORE_FIRST',
  isFragile: false,
  isWettable: false,
  isNeedRefrigerant: false,
  unit: OPTION_NONE,
  netWeight: null,
  grossWeight: null,
  length: null,
  width: null,
  height: null,
  cbm: null,
  boxPerQuantity: null,
  boxNetWeight: null,
  boxGrossWeight: null,
  boxLength: null,
  boxWidth: null,
  boxHeight: null,
  boxCbm: null,
  productType: OPTION_NONE,
  alternativeCode: '',
  productCode: '',
  isNeedCosmeticPacking: false,
  kurlyOrderType: null,
  parcelOrderType: null,
  massDocumentNum: null,
  kurlyKeepType: null,
  isInspection: false,
  stockSafeDate: null,
  deliveryDocumentNum: null,
  skuImageUrl: '',
  packingDirection: OPTION_NONE,
};

const PRODUCT_MODAL_INITIAL_VALUE_STATE: TypeProductModalRequiredInputState = {
  supplierCodes: DEFAULT_INPUT_STATUS,
  skuCode: DEFAULT_INPUT_STATUS,
  skuName: DEFAULT_INPUT_STATUS,
  useMethodCode: DEFAULT_INPUT_STATUS,
  shelfLifeType: DEFAULT_INPUT_STATUS,
  availableDays: DEFAULT_INPUT_STATUS,
  sellAvailableDays: DEFAULT_INPUT_STATUS,
  sellDeadlineDays: DEFAULT_INPUT_STATUS,
  temperatureType: DEFAULT_INPUT_STATUS,
  mainCategoryCode: DEFAULT_INPUT_STATUS,
  middleCategoryCode: DEFAULT_INPUT_STATUS,
  thirdCategoryCode: DEFAULT_INPUT_STATUS,
  isNeedRefrigerant: DEFAULT_INPUT_STATUS,
  unit: DEFAULT_INPUT_STATUS,
  netWeight: DEFAULT_INPUT_STATUS,
  grossWeight: DEFAULT_INPUT_STATUS,
  length: DEFAULT_INPUT_STATUS,
  width: DEFAULT_INPUT_STATUS,
  height: DEFAULT_INPUT_STATUS,
  cbm: DEFAULT_INPUT_STATUS,
  boxPerQuantity: DEFAULT_INPUT_STATUS,
  boxNetWeight: DEFAULT_INPUT_STATUS,
  boxGrossWeight: DEFAULT_INPUT_STATUS,
  boxLength: DEFAULT_INPUT_STATUS,
  boxWidth: DEFAULT_INPUT_STATUS,
  boxHeight: DEFAULT_INPUT_STATUS,
  boxCbm: DEFAULT_INPUT_STATUS,
  productType: DEFAULT_INPUT_STATUS,
  alternativeCode: DEFAULT_INPUT_STATUS,
  productCode: DEFAULT_INPUT_STATUS,
  isNeedCosmeticPacking: DEFAULT_INPUT_STATUS,
  kurlyOrderType: DEFAULT_INPUT_STATUS,
  parcelOrderType: DEFAULT_INPUT_STATUS,
  massDocumentNum: DEFAULT_INPUT_STATUS,
  isInspection: DEFAULT_INPUT_STATUS,
  stockSafeDate: DEFAULT_INPUT_STATUS,
  packingDirection: DEFAULT_INPUT_STATUS,
};

const DEFAULT_PRODUCT_MODAL_ERROR_FIELD_REFS: TypeProductModalErrorFieldRefs = {
  supplierCodes: null,
  skuCode: null,
  skuName: null,
  useMethodCode: null,
  shelfLifeType: null,
  availableDays: null,
  sellAvailableDays: null,
  sellDeadlineDays: null,
  temperatureType: null,
  mainCategoryCode: null,
  middleCategoryCode: null,
  thirdCategoryCode: null,
  isNeedRefrigerant: null,
  unit: null,
  netWeight: null,
  grossWeight: null,
  length: null,
  width: null,
  height: null,
  cbm: null,
  boxPerQuantity: null,
  boxNetWeight: null,
  boxGrossWeight: null,
  boxLength: null,
  boxWidth: null,
  boxHeight: null,
  boxCbm: null,
  productType: null,
  alternativeCode: null,
  productCode: null,
  isNeedCosmeticPacking: null,
  kurlyOrderType: null,
  parcelOrderType: null,
  massDocumentNum: null,
  isInspection: null,
  stockSafeDate: null,
  packingDirection: null,
};

type TypeProductRegisterModalSkuCodeState = {
  isValid: boolean;
  validationMessage: string;
  status: 'default' | 'error';
};

type TypeProductStore = {
  /** [상품등록/수정] 상품 등록/상세 모달 > 입력값 validation 상태 */
  productModalRequiredValueStatus: TypeProductModalRequiredInputState;
  setProductModalRequiredValueStatus: (updatedValue: Partial<TypeProductModalRequiredInputState>) => void;
  resetProductModalRequiredValueStatus: (
    keys?: keyof TypeProductModalRequiredInputState | (keyof TypeProductModalRequiredInputState)[]
  ) => void;

  /** [상품등록/수정] 상품 등록/상세 모달 > 상품코드 입력값 validation 상태 */
  productModalSkuCodeStatus: TypeProductRegisterModalSkuCodeState;
  setProductModalSkuCodeStatus: (updatedValue: Partial<TypeProductRegisterModalSkuCodeState>) => void;
  resetProductModalSkuCodeStatus: () => void;

  /** [상품등록/수정] 상품 등록 모달 > 입력 값 */
  productRegisterModalValue: TypeProductModalInput;
  setProductRegisterModalValue: (updatedValue: Partial<TypeProductModalInput>) => void;
  resetProductRegisterModalValue: () => void;

  /** [상품등록/수정, 운영상품조회] 상품 상세 모달 > 입력 값 */
  productDetailModalValue: TypeProductModalInput;
  setProductDetailModalValue: (updatedValue: Partial<TypeProductModalInput>) => void;

  /** [상품등록/수정] 에러 필드 포커스 처리를 위한 refs 관리 */
  focusFieldRefs: TypeProductModalErrorFieldRefs;
  setFocusFieldRefs: (fieldKey: keyof TypeProductModalErrorFieldRefs, element: HTMLElement | null) => void;
  resetFocusFieldRefs: () => void;
};

/**
 * 상품 관리 스토어
 */
export const useProductStore = create<TypeProductStore>((set, get) => ({
  productModalRequiredValueStatus: PRODUCT_MODAL_INITIAL_VALUE_STATE,
  setProductModalRequiredValueStatus: (updatedValue) =>
    set((state) => ({
      productModalRequiredValueStatus: { ...state.productModalRequiredValueStatus, ...updatedValue },
    })),
  resetProductModalRequiredValueStatus: (
    keys?: keyof TypeProductModalRequiredInputState | (keyof TypeProductModalRequiredInputState)[]
  ) =>
    set((state) => {
      const defaultState = PRODUCT_MODAL_INITIAL_VALUE_STATE;

      if (!keys) {
        return { productModalRequiredValueStatus: defaultState };
      }

      if (Array.isArray(keys)) {
        const updateState = { ...state.productModalRequiredValueStatus };
        keys.forEach((key) => {
          const currentState = state.productModalRequiredValueStatus[key];
          if (defaultState[key] && currentState) {
            updateState[key] = {
              status: 'default',
              message: currentState.message,
            };
          }
        });
        return { productModalRequiredValueStatus: updateState };
      }

      const key = keys;
      const currentState = state.productModalRequiredValueStatus[key];
      if (defaultState[key] && currentState) {
        return {
          productModalRequiredValueStatus: {
            ...state.productModalRequiredValueStatus,
            [key]: {
              status: 'default',
              message: currentState.message,
            },
          },
        };
      }

      return state;
    }),

  productModalSkuCodeStatus: {
    isValid: false,
    validationMessage: '',
    status: 'default',
  },
  setProductModalSkuCodeStatus: (updatedValue) =>
    set((state) => ({
      productModalSkuCodeStatus: { ...state.productModalSkuCodeStatus, ...updatedValue },
    })),
  resetProductModalSkuCodeStatus: () =>
    set({
      productModalSkuCodeStatus: {
        isValid: false,
        validationMessage: '',
        status: 'default',
      },
    }),

  productRegisterModalValue: PRODUCT_MODAL_INITIAL_VALUE,
  setProductRegisterModalValue: (updatedValue) =>
    set((state) => ({ productRegisterModalValue: { ...state.productRegisterModalValue, ...updatedValue } })),
  resetProductRegisterModalValue: () => set({ productRegisterModalValue: PRODUCT_MODAL_INITIAL_VALUE }),

  productDetailModalValue: PRODUCT_MODAL_INITIAL_VALUE,
  setProductDetailModalValue: (updatedValue) =>
    set((state) => ({ productDetailModalValue: { ...state.productDetailModalValue, ...updatedValue } })),

  focusFieldRefs: DEFAULT_PRODUCT_MODAL_ERROR_FIELD_REFS,
  setFocusFieldRefs: (fieldKey: keyof TypeProductModalErrorFieldRefs, element: HTMLElement | null) => {
    get().focusFieldRefs[fieldKey] = element;
  },
  resetFocusFieldRefs: () => {
    const refs = get().focusFieldRefs;
    Object.keys(refs).forEach((key) => {
      refs[key as keyof TypeProductModalErrorFieldRefs] = null;
    });
  },
}));
